import {
  birthYearRange,
  participantInputSchema,
  participantListSchema,
  type Participant,
  type ParticipantInput,
} from '@koderek/shared';
import { zodResolver } from '@hookform/resolvers/zod';
import {
  ActionIcon,
  Alert,
  Avatar,
  Badge,
  Button,
  Group,
  List,
  Modal,
  Paper,
  SegmentedControl,
  Stack,
  Text,
  TextInput,
  Tooltip,
} from '@mantine/core';
import { useMutation } from '@tanstack/react-query';
import { useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { Link } from 'react-router';
import type { z } from 'zod';
import { api } from '../../api/client';
import { ErrorAlert } from '../../components/ErrorAlert';
import { IconPencil, IconTrash } from '../../components/icons';
import { t } from '../../texts/pl';
import { editionLabel, formatDate } from '../editions/format';
import classes from './ParticipantsManager.module.css';

type FormValues = z.input<typeof participantInputSchema>;

interface Props {
  participants: Participant[];
  /** `/participants` (opiekun) albo `/staff/accounts/{id}/participants` (obsługa, US-407). */
  basePath: string;
  onChange: (participants: Participant[]) => void;
  /** Usuwanie: opiekun i admin, nie obsługa (F-403). */
  canRemove: boolean;
  /** Dane opiekuna zmienia się w danych konta (US-208) – link tylko w widoku opiekuna. */
  guardianEditHref?: string;
}

/** US-401…US-404: lista osób z konta, dodawanie, poprawianie i usuwanie. */
export function ParticipantsManager({ participants, basePath, onChange, canRemove, guardianEditHref }: Props) {
  const [editing, setEditing] = useState<Participant | 'new' | null>(null);
  const [removing, setRemoving] = useState<Participant | null>(null);
  const [added, setAdded] = useState(false);

  return (
    <Stack gap="sm">
      {participants.map((p) => {
        const name = `${p.firstName} ${p.lastName}`;
        const details = [
          p.kind === 'child' && p.birthYear ? t.participants.born(p.birthYear) : null,
          p.changedByStaff ? t.participants.changedByStaff : null,
        ].filter(Boolean);
        return (
          <Paper key={p.id} withBorder radius="lg" className={classes.card}>
            <Group justify="space-between" wrap="nowrap" gap="sm">
              <Group gap="md" wrap="nowrap" miw={0}>
                <Avatar color="orange" radius="xl" size={44} className={classes.avatar}>
                  {p.firstName.slice(0, 1).toUpperCase()}
                </Avatar>
                <Stack gap={0} miw={0}>
                  <Text fw={600} truncate>
                    {name}
                  </Text>
                  {details.length ? (
                    <Text size="sm" c="dimmed">
                      {details.join(' · ')}
                    </Text>
                  ) : null}
                </Stack>
              </Group>
              <Group gap="xs" wrap="nowrap">
                <Badge variant="light" radius="xl" tt="none" fw={500} color={p.isGuardian ? 'violet' : 'gray'} visibleFrom="xs">
                  {p.isGuardian ? (p.kind === 'child' ? t.participants.guardianStudent : t.participants.guardian) : p.kind === 'child' ? t.participants.kindChild : t.participants.kindAdult}
                </Badge>
                {p.isGuardian ? (
                  guardianEditHref ? (
                    <Tooltip label={t.participants.guardianInfo} multiline w={220}>
                      <ActionIcon component={Link} to={guardianEditHref} variant="subtle" color="gray" aria-label={t.participants.editOf(name)}>
                        <IconPencil size={18} />
                      </ActionIcon>
                    </Tooltip>
                  ) : null
                ) : (
                  <>
                    <ActionIcon variant="subtle" color="gray" aria-label={t.participants.editOf(name)} onClick={() => setEditing(p)}>
                      <IconPencil size={18} />
                    </ActionIcon>
                    {canRemove ? (
                      <ActionIcon variant="subtle" color="red" aria-label={t.participants.removeOf(name)} onClick={() => setRemoving(p)}>
                        <IconTrash size={18} />
                      </ActionIcon>
                    ) : null}
                  </>
                )}
              </Group>
            </Group>
          </Paper>
        );
      })}
      {added ? (
        <Alert color="green" role="status" withCloseButton onClose={() => setAdded(false)}>
          {t.participants.afterAdd}
        </Alert>
      ) : null}
      <Button variant="light" w="fit-content" onClick={() => setEditing('new')}>
        {t.participants.add}
      </Button>

      <Modal
        opened={editing !== null}
        onClose={() => setEditing(null)}
        title={editing === 'new' ? t.participants.addTitle : t.participants.editTitle}
      >
        {editing !== null ? (
          <ParticipantForm
            participant={editing === 'new' ? null : editing}
            basePath={basePath}
            onSaved={(list) => {
              onChange(list);
              setAdded(editing === 'new');
              setEditing(null);
            }}
          />
        ) : null}
      </Modal>

      <Modal opened={removing !== null} onClose={() => setRemoving(null)} title={t.participants.removeTitle}>
        {removing ? (
          <RemoveParticipant
            participant={removing}
            basePath={basePath}
            onCancel={() => setRemoving(null)}
            onRemoved={(list) => {
              onChange(list);
              setRemoving(null);
            }}
          />
        ) : null}
      </Modal>
    </Stack>
  );
}

function ParticipantForm({
  participant,
  basePath,
  onSaved,
}: {
  participant: Participant | null;
  basePath: string;
  onSaved: (list: Participant[]) => void;
}) {
  const range = birthYearRange(new Date().getFullYear());
  const form = useForm<FormValues, unknown, ParticipantInput>({
    resolver: zodResolver(participantInputSchema),
    defaultValues: {
      firstName: participant?.firstName ?? '',
      lastName: participant?.lastName ?? '',
      kind: participant?.kind ?? 'child',
      birthYear: participant?.birthYear ?? undefined,
    },
  });
  const save = useMutation({
    mutationFn: (body: ParticipantInput) =>
      participant
        ? api('PATCH', `${basePath}/${participant.id}`, { body, schema: participantListSchema })
        : api('POST', basePath, { body, schema: participantListSchema }),
    onSuccess: (res) => onSaved(res.participants),
  });
  const kind = form.watch('kind');

  return (
    <form onSubmit={form.handleSubmit((values) => save.mutate(values))} noValidate>
      <Stack gap="sm">
        <TextInput label={t.fields.firstName} required {...form.register('firstName')} error={form.formState.errors.firstName?.message} />
        <TextInput label={t.fields.lastName} required {...form.register('lastName')} error={form.formState.errors.lastName?.message} />
        <Stack gap={4}>
          <Text size="sm" fw={500}>
            {t.participants.kind}
          </Text>
          <Controller
            control={form.control}
            name="kind"
            render={({ field }) => (
              <SegmentedControl
                value={field.value}
                onChange={field.onChange}
                data={[
                  { value: 'child', label: t.participants.kindChild },
                  { value: 'adult', label: t.participants.kindAdult },
                ]}
              />
            )}
          />
        </Stack>
        {kind === 'child' ? (
          <TextInput
            label={t.participants.birthYear}
            description={t.participants.birthYearHint(range.min, range.max)}
            type="number"
            inputMode="numeric"
            min={range.min}
            max={range.max}
            required
            {...form.register('birthYear')}
            error={form.formState.errors.birthYear?.message}
          />
        ) : null}
        {save.isError ? <ErrorAlert error={save.error} /> : null}
        <Button type="submit" loading={save.isPending}>
          {t.participants.save}
        </Button>
      </Stack>
    </form>
  );
}

/** US-404 AC-1: potwierdzenie pokazuje edycje, z których osoba zostanie wycofana. */
function RemoveParticipant({
  participant,
  basePath,
  onCancel,
  onRemoved,
}: {
  participant: Participant;
  basePath: string;
  onCancel: () => void;
  onRemoved: (list: Participant[]) => void;
}) {
  const remove = useMutation({
    mutationFn: () => api('DELETE', `${basePath}/${participant.id}`, { schema: participantListSchema }),
    onSuccess: (res) => onRemoved(res.participants),
  });
  return (
    <Stack gap="sm">
      <Text>{t.participants.removeText(`${participant.firstName} ${participant.lastName}`)}</Text>
      {participant.upcomingEditions.length ? (
        <>
          <Text size="sm">{t.participants.removeEditions}</Text>
          <List size="sm">
            {participant.upcomingEditions.map((e) => (
              <List.Item key={e.slug}>
                {editionLabel(e)}, {formatDate(e.date)}
              </List.Item>
            ))}
          </List>
        </>
      ) : null}
      {remove.isError ? <ErrorAlert error={remove.error} /> : null}
      <Group justify="flex-end">
        <Button variant="default" onClick={onCancel}>
          {t.registration.cancel}
        </Button>
        <Button color="red" loading={remove.isPending} onClick={() => remove.mutate()}>
          {t.participants.removeConfirm}
        </Button>
      </Group>
    </Stack>
  );
}
